/**
 * useCollapsedSections.ts
 * Persisted collapse/expand state for the Machine Schedule grid's lab-section
 * groups (FEAT-2026-0014), extracted from MachineScheduleGrid.tsx (600-line
 * cap, CODING-STANDARDS). Behavior unchanged: per-section toggle, one-click
 * expand/collapse-all, localStorage persistence across visits.
 */

import { useCallback, useState } from 'react';

/** localStorage key for which lab sections are collapsed (persists across visits). */
const COLLAPSE_STORAGE_KEY = 'moftaki.machineSchedule.collapsedSections';

function loadCollapsedSections(): Set<string> {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? (arr as string[]) : []);
  } catch {
    return new Set();
  }
}

export function useCollapsedSections(sectionNames: string[], forceExpandAll: boolean) {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(loadCollapsedSections);

  const persistCollapsed = useCallback((next: Set<string>) => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify([...next]));
    } catch {
      /* ignore quota / private-mode errors */
    }
  }, []);

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      persistCollapsed(next);
      return next;
    });
  }, [persistCollapsed]);

  const allCollapsed = sectionNames.length > 0 && !forceExpandAll
    && sectionNames.every((s) => collapsedSections.has(s));

  const setAllCollapsed = useCallback(
    (collapse: boolean) => {
      const next = collapse ? new Set(sectionNames) : new Set<string>();
      setCollapsedSections(next);
      persistCollapsed(next);
    },
    [sectionNames, persistCollapsed],
  );

  return { collapsedSections, toggleSection, allCollapsed, setAllCollapsed };
}
